import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';

// SVG Icons
const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

interface SystemMetrics {
  timestamp: string;
  responseTime: string;
  database: any;
  system: any;
  process: any;
  environment: any;
}

export default function DashboardTecnicoPage() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiFetch<SystemMetrics>('/api/dev/system-metrics');
      setMetrics(data);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento delle metriche');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadMetrics();
    }, 10000); // Refresh ogni 10 secondi

    return () => clearInterval(interval);
  }, [autoRefresh, loadMetrics]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}g ${hours}h ${minutes}m`;
  };

  if (error) {
    return (
      <Layout>
        <div className="page-header">
          <div className="page-header-text">
            <h2>Dashboard Tecnico</h2>
            <p>Metriche e KPI di sistema</p>
          </div>
        </div>
        <div className="bop-card bop-card__body">
          <div className="alert alert-error">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Dashboard Tecnico</h2>
          <p>Metriche e KPI di sistema</p>
        </div>
        <div className="bop-dash-tecnico__header-actions">
          <label className="bop-dash-tecnico__auto-refresh-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (10s)
          </label>
          <button className="bop-btn bop-btn-secondary" onClick={loadMetrics} disabled={loading}>
            <RefreshIcon />
            Aggiorna
          </button>
        </div>
      </div>

      {loading && !metrics ? (
        <div className="bop-card bop-card__body">
          <div className="bop-dash-tecnico__loading">
            <p className="bop-dash-tecnico__loading-text">Caricamento metriche...</p>
          </div>
        </div>
      ) : metrics ? (
        <>
          {/* Informazioni generali */}
          <div className="bop-card bop-card__body bop-dash-tecnico__section">
            <div className="bop-dash-tecnico__section-header">
              <h3 className="bop-dash-tecnico__section-title">Informazioni Generali</h3>
              <div className="bop-dash-tecnico__timestamp">
                Ultimo aggiornamento: {new Date(metrics.timestamp).toLocaleString('it-IT')} (
                {metrics.responseTime})
              </div>
            </div>

            <div className="bop-dash-tecnico__grid">
              <StatCard label="Ambiente" value={metrics.environment.nodeEnv.toUpperCase()} />
              <StatCard label="Porta" value={metrics.environment.port} />
              <StatCard label="Hostname" value={metrics.system.hostname} />
              <StatCard label="Uptime" value={formatUptime(metrics.system.uptime)} />
            </div>
          </div>

          {/* Metriche Database */}
          <div className="bop-card bop-card__body bop-dash-tecnico__section">
            <h3 className="bop-dash-tecnico__section-title--with-mb">Database</h3>

            <div className="bop-dash-tecnico__grid--narrow bop-dash-tecnico__grid--mb">
              <StatCard
                label="Utenti"
                value={`${metrics.database.utenti.attivi} / ${metrics.database.utenti.totale}`}
                subtext="attivi / totali"
              />
              <StatCard
                label="Società"
                value={`${metrics.database.societa.attive} / ${metrics.database.societa.totale}`}
                subtext="attive / totali"
              />
              <StatCard
                label="Sedi"
                value={`${metrics.database.sedi.attive} / ${metrics.database.sedi.totale}`}
                subtext="attive / totali"
              />
              <StatCard label="Insegne" value={metrics.database.insegne} />
              <StatCard label="Premi" value={metrics.database.premi} />
              <StatCard label="Commesse" value={metrics.database.commesse.totale} />
              <StatCard label="Polizze" value={metrics.database.polizze} />
              <StatCard label="Dipendenti" value={metrics.database.dipendenti} />
              <StatCard
                label="Messaggi"
                value={`${metrics.database.messaggi.nonLetti} / ${metrics.database.messaggi.totale}`}
                subtext="non letti / totali"
              />
            </div>

            {/* Distribuzione ruoli */}
            {metrics.database.utentiByRuolo && metrics.database.utentiByRuolo.length > 0 && (
              <div className="bop-dash-tecnico__subsection">
                <h4 className="bop-dash-tecnico__subsection-title">
                  Distribuzione Ruoli Utenti
                </h4>
                <div className="bop-dash-tecnico__tag-list">
                  {metrics.database.utentiByRuolo.map((r: any) => (
                    <div
                      key={r.ruolo}
                      className={`bop-dash-tecnico__tag ${r.ruolo === 'SUPERADMIN' ? 'bop-dash-tecnico__tag--superadmin' : 'bop-dash-tecnico__tag--info'}`}
                    >
                      <div className="bop-dash-tecnico__tag-label">{r.ruolo}</div>
                      <div className="bop-dash-tecnico__tag-value">{r.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stati commesse */}
            {metrics.database.commesse.byStato && metrics.database.commesse.byStato.length > 0 && (
              <div className="bop-dash-tecnico__subsection">
                <h4 className="bop-dash-tecnico__subsection-title">Stati Commesse</h4>
                <div className="bop-dash-tecnico__tag-list">
                  {metrics.database.commesse.byStato.map((s: any) => (
                    <div
                      key={s.stato}
                      className="bop-dash-tecnico__tag"
                    >
                      <div className="bop-dash-tecnico__tag-label">{s.stato}</div>
                      <div className="bop-dash-tecnico__tag-value">{s._count.stato}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top 10 moduli permessi */}
            {metrics.database.permessi.top10Moduli && metrics.database.permessi.top10Moduli.length > 0 && (
              <div>
                <h4 className="bop-dash-tecnico__subsection-title">
                  Top 10 Moduli per Permessi Assegnati
                </h4>
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Modulo</th>
                        <th className="bop-text-right">Permessi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.database.permessi.top10Moduli.map((p: any, idx: number) => (
                        <tr key={idx}>
                          <td>
                            <code className="bop-dash-tecnico__code">{p.modulo}</code>
                          </td>
                          <td className="bop-text-right bop-dash-tecnico__tag-value">{p.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Metriche Sistema */}
          <div className="bop-card bop-card__body bop-dash-tecnico__section">
            <h3 className="bop-dash-tecnico__section-title--with-mb">Sistema</h3>

            <div className="bop-dash-tecnico__grid bop-dash-tecnico__grid--mb">
              <StatCard label="Platform" value={`${metrics.system.platform} (${metrics.system.arch})`} />
              <StatCard label="Node.js" value={metrics.system.nodeVersion} />
              <StatCard label="CPU Cores" value={metrics.system.cpus} />
              <StatCard
                label="Load Average"
                value={metrics.system.loadAverage.map((l: number) => l.toFixed(2)).join(' / ')}
                subtext="1min / 5min / 15min"
              />
            </div>

            {/* Memoria */}
            <h4 className="bop-dash-tecnico__subsection-title--spaced">Memoria Sistema</h4>
            <div className="bop-dash-tecnico__grid--narrow bop-dash-tecnico__grid--mb">
              <StatCard label="Totale" value={formatBytes(metrics.system.totalMemory)} />
              <StatCard label="Usata" value={formatBytes(metrics.system.usedMemory)} />
              <StatCard label="Libera" value={formatBytes(metrics.system.freeMemory)} />
              <StatCard
                label="Utilizzo"
                value={`${metrics.system.memoryUsagePercent.toFixed(1)}%`}
                highlight={metrics.system.memoryUsagePercent > 80}
              />
            </div>

            {/* Memoria Processo */}
            <h4 className="bop-dash-tecnico__subsection-title--spaced">
              Memoria Processo Node.js (PID: {metrics.process.pid})
            </h4>
            <div className="bop-dash-tecnico__grid--narrow">
              <StatCard label="RSS" value={formatBytes(metrics.process.rss)} subtext="Resident Set Size" />
              <StatCard label="Heap Total" value={formatBytes(metrics.process.heapTotal)} />
              <StatCard label="Heap Used" value={formatBytes(metrics.process.heapUsed)} />
              <StatCard label="External" value={formatBytes(metrics.process.external)} />
            </div>
          </div>

          {/* Configurazione Ambiente */}
          <div className="bop-card bop-card__body">
            <h3 className="bop-dash-tecnico__section-title--with-mb">Configurazione Ambiente</h3>

            <div className="bop-dash-tecnico__grid">
              <ConfigItem label="Database URL" configured={metrics.environment.hasDatabaseUrl} />
              <ConfigItem label="Cloud SQL Connection" configured={metrics.environment.hasCloudSqlConnection} />
              <ConfigItem label="Google OAuth" configured={metrics.environment.hasGoogleCredentials} />
              <ConfigItem label="JWT Secret" configured={metrics.environment.hasJwtSecret} />
              <ConfigItem label="Session Secret" configured={metrics.environment.hasSessionSecret} />
            </div>
          </div>
        </>
      ) : null}
    </Layout>
  );
}

// Componente StatCard
function StatCard({
  label,
  value,
  subtext,
  highlight = false,
}: {
  label: string;
  value: string | number;
  subtext?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`bop-dash-tecnico__stat-card ${highlight ? 'bop-dash-tecnico__stat-card--highlight' : ''}`}>
      <div className="bop-dash-tecnico__stat-label">{label}</div>
      <div className={`bop-dash-tecnico__stat-value ${highlight ? 'bop-dash-tecnico__stat-value--highlight' : ''}`}>{value}</div>
      {subtext && <div className="bop-dash-tecnico__stat-subtext">{subtext}</div>}
    </div>
  );
}

// Componente ConfigItem
function ConfigItem({ label, configured }: { label: string; configured: boolean }) {
  return (
    <div className={`bop-dash-tecnico__config-item ${configured ? 'bop-dash-tecnico__config-item--ok' : 'bop-dash-tecnico__config-item--ko'}`}>
      <div className={`bop-dash-tecnico__config-dot ${configured ? 'bop-dash-tecnico__config-dot--ok' : 'bop-dash-tecnico__config-dot--ko'}`} />
      <div>
        <div className="bop-dash-tecnico__config-label">{label}</div>
        <div className={configured ? 'bop-dash-tecnico__config-status--ok' : 'bop-dash-tecnico__config-status--ko'}>
          {configured ? 'Configurato' : 'Non configurato'}
        </div>
      </div>
    </div>
  );
}
